import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/auth";

const TOKEN_KEY = "token";

function appHomePath(): string {
  const pathPrefix = (import.meta.env.BASE_URL || "/").replace(/\/$/, "");
  return pathPrefix ? `${pathPrefix}/` : "/";
}

/**
 * OAuth redirect target: backend sends users here with ?token= after /api/auth/google/callback.
 * Hydrates the session in-app (no full reload) then navigates to the dashboard.
 */
export default function AuthSuccess() {
  const [, setLocation] = useLocation();
  const { hydrateSession, clearClientSession } = useAuth();
  const [message, setMessage] = useState("Signing you in…");

  useEffect(() => {
    let cancelled = false;

    async function finishOAuth() {
      const params = new URLSearchParams(window.location.search);
      const token = params.get("token")?.trim();
      if (!token) {
        setMessage("Missing sign-in token. Returning to login…");
        clearClientSession();
        window.setTimeout(() => {
          if (!cancelled) setLocation(appHomePath());
        }, 1200);
        return;
      }
      try {
        localStorage.setItem(TOKEN_KEY, token);
      } catch {
        setMessage("Could not save session. Returning to login…");
        clearClientSession();
        window.setTimeout(() => {
          if (!cancelled) setLocation(appHomePath());
        }, 1200);
        return;
      }

      const ok = await hydrateSession();
      if (cancelled) return;
      if (ok) {
        setLocation(appHomePath());
        return;
      }
      setMessage("Could not finish sign-in. Returning to login…");
      clearClientSession();
      window.setTimeout(() => {
        if (!cancelled) setLocation(appHomePath());
      }, 1500);
    }

    void finishOAuth();
    return () => {
      cancelled = true;
    };
  }, [hydrateSession, clearClientSession, setLocation]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-slate-950 text-white font-mono text-sm px-6 text-center">
      <Loader2 className="h-6 w-6 animate-spin text-white/60" aria-hidden />
      <p>{message}</p>
    </div>
  );
}
